import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

const STEPS: {
  title: string;
  body: string;
  entity?: string;
  entityLabel?: string;
  where?: [string, ...string[]];
  whereNote?: string;
  figure?: ReactNode;
}[] = [
  {
    title: 'Turn the CRM on',
    body: 'The CRM is an add-on you enable per workspace. Once it is on, Companies, Contacts, and a Pipeline board appear alongside your Clients; Tasks are always available. Nothing about your billing data changes — your existing customers are already in the funnel as Clients, and the default Clients list keeps showing Client-stage organizations only, so companies still at the prospect stage and open deals never inflate your customer count or revenue metrics.',
  },
  {
    title: 'Capture a prospect',
    body: 'Before a company is a paying customer it sits at the Prospect stage on the Companies page. Create one from a name — a region is not required yet — and optionally a web domain and email. Domains are normalized and deduplicated, so adding a company that already exists surfaces the existing record instead of a duplicate. Add the people you talk to as Contacts on the company; contact emails are unique across the workspace, and each contact tracks its own marketing-email consent.',
    entity: 'contact',
    entityLabel: 'Contact',
  },
  {
    title: 'Bring a spreadsheet of people and companies',
    body: 'Adding contacts one record at a time is still there; a list you already have — a conference export, a customer success sheet — does not have to be typed in again. Import → Contacts and companies accepts a CSV or Excel (.xlsx) file, up to 50MB. Revvly maps columns by fuzzy name rather than exact-name-only; you confirm the mapping, preview the rows, and import. Companies are created or matched by domain or name; contacts land on the right record; an email that already exists in the workspace is skipped rather than duplicated. A large file is split and run from the bottom up, so a mid-file failure does not lose the rows already written. Progress is visible for the whole run, and every batch is logged with who ran it, the file, created counts, and failures.',
    entity: 'contact',
    entityLabel: 'Contact',
    where: ['Import', 'Contacts and companies'],
    whereNote: 'CSV or .xlsx. Excel 97-2003 (.xls) is not supported — save as .xlsx or CSV first.',
  },
  {
    title: "Add the rest of a company's people",
    body: 'Opening a client to add contacts used to mean remembering who else works there, even when Revvly had already seen those addresses on mail or held them as unassigned contacts. The Contacts tab now suggests people at the same domain — unassigned contacts, and addresses that appear in email traffic, ranked by how often and how recently they show up — and you can add one or all of them. An address already held by anyone in the workspace is never suggested, and the domain is checked again on write, so a suggestion cannot quietly attach someone from another company.',
    entity: 'contact',
    entityLabel: 'Contact',
    where: ['Clients', 'open a client', 'Contacts'],
    whereNote:
      'The suggestions sit below the contacts you already have. Nothing is added until you click.',
  },
  {
    title: 'Raise a lead',
    body: 'A Lead is the deal itself — a title, an estimated value and currency, an expected close date, and an owner. Point it at an existing organization or create a brand-new company inline in the same step. New leads land on your default pipeline at its first Open stage unless you choose otherwise. Give it an idempotency key if you create leads over the API and want repeat calls to be safe.',
    entity: 'lead',
    entityLabel: 'Lead',
  },
  {
    title: 'Work the board',
    body: 'Your Pipeline is your sales process as a set of ordered Stages — the default is New → Qualified → Proposal → Won / Lost, and you can rename, recolor, reorder, add, or archive stages freely. Each stage has a kind (Open, Won, or Lost); dragging a lead into a stage sets its status from that kind, so the board and the status never drift apart. Archiving a stage moves its leads somewhere you choose rather than losing them.',
    entity: 'pipeline',
    entityLabel: 'Pipeline',
  },
  {
    title: 'Log the work as it happens',
    body: 'Record everything against the client, contact, or lead it belongs to. Notes are free text and can be pinned. Documents — contracts, proposals, decks — upload and download through short-lived signed links. Emails are captured by the inbox integration or logged by hand, inbound or outbound. Meetings record that you met, and what came of it. Tasks carry a status, a priority, and a due date that can follow a CRM moment. It all merges into one activity timeline, newest work first.',
    entity: 'crmActivity',
    entityLabel: 'Activity timeline',
  },
  {
    title: 'Keep the meetings',
    body: 'A meeting is the record that you met — the time, who was there, where, the topic, and your notes from it. Log one by hand, or connect Calendly and every booking arrives on its own. A meeting carries an outcome rather than just a date: it starts Scheduled and you mark it Held, No-show, or Cancelled, so ‘we have talked to them three times this quarter’ is a question the timeline can answer. Meetings still to come sit in a Coming up block above the timeline and drop into the history once they have started. Revvly is not a calendar and does not sync one — it keeps the engagement record, and leaves the scheduling to whatever you already book with.',
    entity: 'crmActivity',
    entityLabel: 'Activity timeline',
  },
  {
    title: 'Let Calendly fill it in',
    body: 'Connect Calendly once and bookings become meetings without anyone typing them. A booking whose invitee or guest matches a client you already have is recorded against that client; one that matches nobody is ignored unless a Booking intake route is enabled under Settings, in which case it can create a company, contact, or lead from the booking; with no route enabled nothing is created, because a scheduling link is public and anyone can book it. Cancellations and no-shows marked in Calendly follow through to the same meeting. Facts the calendar owns — the time, the attendees, the join link — stay owned by it and are not editable here, so the next update cannot silently overwrite an edit; the topic, notes, and outcome are yours and are never touched.',
    entity: 'crmActivity',
    entityLabel: 'Activity timeline',
  },
  {
    title: 'Read the mail that was synced',
    body: 'Ingested mailbox threads used to be stored and triaged with no dashboard surface. Synced mail now appears on the client alongside the mail you sent from Revvly: you can open a thread, read the body, and it sits on the CRM timeline with everything else. Connect Gmail from your own Account — each operator connects their mailbox, not a workspace-wide one. Conversation and Gmail stay behind the CRM flag, so a workspace that has not turned CRM on does not grow a mail surface it cannot use. Looking at a sent email in Conversation does not count as the recipient opening it; a tracked send nobody has opened says Not opened rather than looking like a send with no status.',
    entity: 'crmActivity',
    entityLabel: 'Activity timeline',
    where: ['Clients', 'open a client', 'Conversation'],
    whereNote:
      'On a prospect the conversation is the Overview column. Connect Gmail under Account — Drive folders stay in Settings → Integrations. With the Work Inbox on, the same Gmail connection also feeds your own sorted inbox; see Work your inbox.',
  },
  {
    title: 'Convert a won deal',
    body: "Only a Won lead can be converted. Converting flips the lead's organization to a Client in one all-or-nothing step and, optionally, links the subscription that closed the deal. You cannot set the Client stage by hand. The other way a company becomes a Client is when a subscription is created for it — and once a record is a Client it never regresses to an earlier stage. From here the account lives in the revenue core: subscriptions, invoices, and lifecycle events.",
    entity: 'client',
    entityLabel: 'Client',
    figure: (
      <Figure
        title="Two routes to Client"
        caption="A company becomes a Client by converting a won lead or by getting a subscription, never by hand, and it never moves back."
      >
        <Flow
          steps={[
            { title: 'Lead on the board', text: 'An Open stage on your pipeline.' },
            { title: 'Lead is Won', text: 'Dragged into a Won stage.' },
            {
              fork: [
                {
                  title: 'Convert the won lead',
                  text: 'One all-or-nothing step. Optionally links the subscription.',
                  tone: 'accent',
                },
                {
                  title: 'Create a subscription',
                  text: 'Creating one for the company also makes it a Client.',
                },
              ],
            },
            {
              title: 'Client',
              text: 'Never regresses to an earlier stage.',
              tone: 'accent',
            },
          ]}
        />
      </Figure>
    ),
  },
  {
    title: 'Stages, owners, and dead ends',
    body: 'Every organization and every contact carries its own lifecycle stage — Prospect, In conversation, Client, or Disqualified — and they move independently: a contact can be Disqualified at a company that is otherwise a Client. Move records between the early stages directly, mark cold ones Disqualified, and assign an owner to any organization or contact so it is always clear who holds the relationship. Client is never set by hand: it is reached by converting a won lead or by creating a subscription for the company.',
    entity: 'client',
    entityLabel: 'Client',
  },
  {
    title: 'Let the Assistant drive it',
    body: 'The Revvly Assistant reads and writes the CRM. Ask it to catch you up on an account and it reads the merged timeline; ask it to search and it turns a name into the right client, contact, or lead. It can create prospects, contacts, and leads, move a lead across stages, log notes, emails, and meetings, and reassign owners — always as a confirmation card you approve first. Anything it creates is flagged as AI-assisted and keeps the rationale for why.',
    entity: 'crmActivity',
    entityLabel: 'Activity timeline',
  },
];

export function CrmGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Run your sales pipeline (CRM) — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Run your sales pipeline</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Run your <em>sales pipeline</em>.
        </h1>
        <p className="dp-oneliner">
          Revvly&rsquo;s CRM lives beside your billing data — the same accounts, from first contact
          to signed customer. Capture prospects or import a spreadsheet, work deals across a board,
          log every touch and every meeting, read the mail that was synced, and convert a won deal
          into a Client without leaving the tool.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.where && <UiPath steps={step.where} note={step.whereNote} />}
          {step.figure}
          {step.entity && (
            <ul className="dp-connects" style={{ marginTop: 14 }}>
              <li>
                <Link
                  to={`/docs/concepts/${step.entity}`}
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  {step.entityLabel} concept ›
                </Link>
              </li>
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
